/**
 * admin_micoapp_logs.data 摘要：解析为可读说明，不展示原始 JSON。
 */

const MSG_KEYS = [
  "errmsg_zh",
  "errmsg",
  "error_msg",
  "err_msg",
  "msg",
  "message",
  "reason",
  "fail_reason",
  "description",
  "desc",
  "detail",
  "info",
  "result_msg",
  "status_msg",
  "return_msg",
  "title",
  "content",
  "text",
  "memo",
  "remark",
  "audit_reason",
  "reject_reason",
  "hint",
  "suggest",
  "nickname",
];

const NEST_KEYS = ["data", "response", "result", "payload", "body", "info", "detail"];

/** 微信认证审核派单通知（组件回调等场景），文案结构与微信官方说明一致 */
const WX_VERIFY_DISPATCH_EVENT = "wx_verify_dispatch";

/** 官方咨询信息（与微信认证说明一致，便于用户联系） */
const WX_VERIFY_DISPATCH_FOOTER =
  "咨询电话：028-85182726、028-67686807，邮箱：wx@anquan.org，咨询时间：周一至周五（工作日）9：00-18：00。";

/**
 * @param {string} contact 认证联系人（Contact）
 * @param {string} provider 第三方审核机构名称（Provider）
 */
function buildWxVerifyDispatchLine(contact, provider) {
  const p =
    provider != null && String(provider).trim() !== ""
      ? String(provider).trim()
      : "";
  const c =
    contact != null && String(contact).trim() !== ""
      ? String(contact).trim()
      : "";

  const agencyPart = p
    ? `第三方审核机构「${p}」`
    : "第三方审核机构";

  const contactBracket = c ? `（${c}）` : "";

  const firstPara =
    `你的微信认证申请已委托给${agencyPart}进行微信认证审核，后续该公司将与认证联系人${contactBracket}联系，请提醒认证联系人在审核期间保持电话畅通，切勿屏蔽第三方审核机构电话，以免影响正常审核进度。`;

  return `${firstPara}\n\n${WX_VERIFY_DISPATCH_FOOTER}`;
}

/**
 * 在嵌套对象/数组中查找 Event / event / Events / events 等于指定值（小写比较）的节点
 * @param {string} eventNameLower 如 wx_verify_dispatch、user_enter_tempsession
 */
function findEventContextRecursive(obj, eventNameLower, depth) {
  const d = depth == null ? 0 : depth;
  if (d > 5 || !isPlainObject(obj)) return null;
  const ev =
    obj.Event != null
      ? obj.Event
      : obj.event != null
        ? obj.event
        : obj.Events != null
          ? obj.Events
          : obj.events;
  if (
    ev != null &&
    String(ev).toLowerCase() === eventNameLower
  ) {
    return obj;
  }
  const keys = Object.keys(obj);
  for (let i = 0; i < keys.length; i++) {
    const val = obj[keys[i]];
    if (isPlainObject(val)) {
      const found = findEventContextRecursive(val, eventNameLower, d + 1);
      if (found) return found;
    }
    if (Array.isArray(val)) {
      for (let j = 0; j < val.length; j++) {
        if (isPlainObject(val[j])) {
          const found = findEventContextRecursive(val[j], eventNameLower, d + 1);
          if (found) return found;
        }
      }
    }
  }
  return null;
}

/** 从 JSON 结构解析审核派单摘要 */
function summarizeWxVerifyDispatchFromObject(obj) {
  const ctx = findEventContextRecursive(obj, WX_VERIFY_DISPATCH_EVENT, 0);
  if (!ctx) return null;
  const contact = ctx.Contact != null ? ctx.Contact : ctx.contact;
  const provider = ctx.Provider != null ? ctx.Provider : ctx.provider;
  return buildWxVerifyDispatchLine(contact, provider);
}

/** 用户点击小程序内客服会话按钮进入临时会话（微信推送 user_enter_tempsession） */
const USER_ENTER_TEMPSESSION_EVENT = "user_enter_tempsession";

function formatSessionFromHint(ctx) {
  if (!ctx || !isPlainObject(ctx)) return "";
  const raw =
    ctx.SessionFrom != null
      ? ctx.SessionFrom
      : ctx.session_from != null
        ? ctx.session_from
        : null;
  if (raw == null) return "";
  const sf = String(raw).trim();
  if (sf === "") return "";
  const n = Number(sf);
  if (!Number.isNaN(n)) {
    /** 1 与主文案一致，不再重复；2 等为补充说明 */
    if (n === 1) return "";
    if (n === 2) return "\n会话入口为公众号相关场景（非小程序内客服按钮）。";
  }
  return `\n会话场景值（SessionFrom）：${sf}。`;
}

function buildUserEnterTempsessionSummary(ctx) {
  const base =
    "有用户发起客服请求。在微信小程序内点击「客服会话按钮」进入客服会话（临时会话）时，微信服务器会推送本事件。";
  return ctx ? `${base}${formatSessionFromHint(ctx)}` : base;
}

function summarizeUserEnterTempsessionFromObject(obj) {
  const ctx = findEventContextRecursive(obj, USER_ENTER_TEMPSESSION_EVENT, 0);
  if (!ctx) return null;
  return buildUserEnterTempsessionSummary(ctx);
}

/** 微信认证费用支付成功 */
const WX_VERIFY_PAY_SUCC_EVENT = "wx_verify_pay_succ";

function summarizeWxVerifyPaySuccFromObject(obj) {
  const ctx = findEventContextRecursive(obj, WX_VERIFY_PAY_SUCC_EVENT, 0);
  if (!ctx) return null;
  return "微信认证费用支付成功。";
}

/** 纯文本 / querystring：Event(s)=wx_verify_pay_succ */
function trySummarizeWxVerifyPaySuccPlainText(s) {
  if (!s || typeof s !== "string" || !s.includes("wx_verify_pay_succ")) {
    return null;
  }
  try {
    const tail = s.indexOf("?") >= 0 ? s.split("?").slice(1).join("?") : s;
    const q = new URLSearchParams(tail.replace(/^&/, ""));
    const ev =
      q.get("Event") ||
      q.get("event") ||
      q.get("Events") ||
      q.get("events");
    if (!ev || String(ev).toLowerCase() !== WX_VERIFY_PAY_SUCC_EVENT) {
      return null;
    }
    return "微信认证费用支付成功。";
  } catch {
    return null;
  }
}

/** 小程序名称审核成功 */
const WXA_NICKNAME_AUDIT_EVENT = "wxa_nickname_audit";

function summarizeWxaNicknameAuditFromObject(obj) {
  const ctx = findEventContextRecursive(obj, WXA_NICKNAME_AUDIT_EVENT, 0);
  if (!ctx) return null;
  return "小程序名称审核成功。";
}

/** 纯文本 / querystring：Event(s)=wxa_nickname_audit */
function trySummarizeWxaNicknameAuditPlainText(s) {
  if (!s || typeof s !== "string" || !s.includes("wxa_nickname_audit")) {
    return null;
  }
  try {
    const tail = s.indexOf("?") >= 0 ? s.split("?").slice(1).join("?") : s;
    const q = new URLSearchParams(tail.replace(/^&/, ""));
    const ev =
      q.get("Event") ||
      q.get("event") ||
      q.get("Events") ||
      q.get("events");
    if (!ev || String(ev).toLowerCase() !== WXA_NICKNAME_AUDIT_EVENT) {
      return null;
    }
    return "小程序名称审核成功。";
  } catch {
    return null;
  }
}

/** 微信认证成功（小程序审核通过等场景） */
const WEAPP_AUDIT_SUCCESS_EVENT = "weapp_audit_success";

function summarizeWeappAuditSuccessFromObject(obj) {
  const ctx = findEventContextRecursive(obj, WEAPP_AUDIT_SUCCESS_EVENT, 0);
  if (!ctx) return null;
  return "微信认证成功。";
}

/** 纯文本 / querystring：Event(s)=weapp_audit_success */
function trySummarizeWeappAuditSuccessPlainText(s) {
  if (!s || typeof s !== "string" || !s.includes("weapp_audit_success")) {
    return null;
  }
  try {
    const tail = s.indexOf("?") >= 0 ? s.split("?").slice(1).join("?") : s;
    const q = new URLSearchParams(tail.replace(/^&/, ""));
    const ev =
      q.get("Event") ||
      q.get("event") ||
      q.get("Events") ||
      q.get("events");
    if (!ev || String(ev).toLowerCase() !== WEAPP_AUDIT_SUCCESS_EVENT) {
      return null;
    }
    return "微信认证成功。";
  } catch {
    return null;
  }
}

/** 小程序发布审核失败 */
const WEAPP_AUDIT_FAIL_EVENT = "weapp_audit_fail";

function normalizeWeappAuditReasonText(raw) {
  if (raw == null) return "";
  let s = String(raw);
  s = s.replace(/<br\s*\/?>/gi, "\n");
  s = s.replace(/<\/?[^>]+>/g, "");
  return s.replace(/\n{3,}/g, "\n\n").trim();
}

function buildWeappAuditFailSummary(ctx) {
  if (!ctx || !isPlainObject(ctx)) return null;
  const lines = ["小程序发布审核失败。"];
  const reason = ctx.Reason != null ? ctx.Reason : ctx.reason;
  if (reason != null && String(reason).trim() !== "") {
    lines.push(`原因：\n${normalizeWeappAuditReasonText(reason)}`);
  }
  const ft = ctx.FailTime != null ? ctx.FailTime : ctx.failTime;
  const ct = ctx.CreateTime != null ? ctx.CreateTime : ctx.create_time;
  if (ft != null && String(ft).trim() !== "") {
    lines.push(`审核失败时间（FailTime）：${String(ft).trim()}`);
  }
  if (
    ct != null &&
    String(ct).trim() !== "" &&
    String(ct).trim() !== String(ft != null ? ft : "").trim()
  ) {
    lines.push(`推送时间（CreateTime）：${String(ct).trim()}`);
  } else if (ct != null && String(ct).trim() !== "" && (ft == null || String(ft).trim() === "")) {
    lines.push(`时间（CreateTime）：${String(ct).trim()}`);
  }
  const shot = ctx.ScreenShot != null ? ctx.ScreenShot : ctx.screen_shot;
  if (shot != null && String(shot).trim() !== "") {
    const t = String(shot).trim();
    lines.push(
      `审核截图凭证（ScreenShot）：${
        t.length > 96 ? `${t.slice(0, 96)}…` : t
      }`
    );
  }
  return lines.join("\n");
}

function summarizeWeappAuditFailFromObject(obj) {
  const ctx = findEventContextRecursive(obj, WEAPP_AUDIT_FAIL_EVENT, 0);
  if (!ctx) return null;
  return buildWeappAuditFailSummary(ctx);
}

/** querystring：weapp_audit_fail（Reason 可能很长） */
function trySummarizeWeappAuditFailPlainText(s) {
  if (!s || typeof s !== "string" || !s.includes("weapp_audit_fail")) {
    return null;
  }
  try {
    const tail = s.indexOf("?") >= 0 ? s.split("?").slice(1).join("?") : s;
    const q = new URLSearchParams(tail.replace(/^&/, ""));
    const ev =
      q.get("Event") ||
      q.get("event") ||
      q.get("Events") ||
      q.get("events");
    if (!ev || String(ev).toLowerCase() !== WEAPP_AUDIT_FAIL_EVENT) {
      return null;
    }
    const fake = {
      Reason: q.get("Reason") || q.get("reason"),
      FailTime: q.get("FailTime"),
      CreateTime: q.get("CreateTime"),
      ScreenShot: q.get("ScreenShot"),
    };
    return buildWeappAuditFailSummary(fake);
  } catch {
    return null;
  }
}

/** 小程序付费能力用量告警 */
const CHARGE_SERVICE_QUOTA_NOTIFY_EVENT = "charge_service_quota_notify";

function buildChargeServiceQuotaNotifySummary(ctx) {
  if (!ctx || !isPlainObject(ctx)) return null;
  const lines = ["小程序付费能力用量告警。"];
  const spuName =
    ctx.spu_name != null
      ? String(ctx.spu_name).trim()
      : ctx.spuName != null
        ? String(ctx.spuName).trim()
        : "";
  const spuId =
    ctx.spu_id != null
      ? String(ctx.spu_id).trim()
      : ctx.spuId != null
        ? String(ctx.spuId).trim()
        : "";
  if (spuName || spuId) {
    const namePart = spuName || "—";
    const idPart = spuId ? `（spu_id：${spuId}）` : "";
    lines.push(`组件：${namePart}${idPart}`);
  }
  const used =
    ctx.total_used_quota != null
      ? ctx.total_used_quota
      : ctx.totalUsedQuota != null
        ? ctx.totalUsedQuota
        : null;
  const total =
    ctx.total_quota != null
      ? ctx.total_quota
      : ctx.totalQuota != null
        ? ctx.totalQuota
        : null;
  if (used != null || total != null) {
    lines.push(
      `用量：${used != null ? used : "—"} / 配额：${total != null ? total : "—"}`
    );
  }
  const et =
    ctx.event_type != null
      ? ctx.event_type
      : ctx.eventType != null
        ? ctx.eventType
        : null;
  if (et != null && String(et).trim() !== "") {
    lines.push(`告警类型（event_type）：${String(et).trim()}`);
  }
  const appid =
    ctx.appid != null
      ? String(ctx.appid).trim()
      : ctx.AppId != null
        ? String(ctx.AppId).trim()
        : "";
  if (appid) {
    lines.push(`AppID：${appid}`);
  }
  const ct =
    ctx.CreateTime != null
      ? ctx.CreateTime
      : ctx.create_time != null
        ? ctx.create_time
        : null;
  if (ct != null && String(ct).trim() !== "") {
    lines.push(`时间（CreateTime）：${String(ct).trim()}`);
  }
  return lines.join("\n");
}

function summarizeChargeServiceQuotaNotifyFromObject(obj) {
  const ctx = findEventContextRecursive(
    obj,
    CHARGE_SERVICE_QUOTA_NOTIFY_EVENT,
    0
  );
  if (!ctx) return null;
  return buildChargeServiceQuotaNotifySummary(ctx);
}

/** 纯文本 / querystring：charge_service_quota_notify */
function trySummarizeChargeServiceQuotaNotifyPlainText(s) {
  if (!s || typeof s !== "string" || !s.includes("charge_service_quota_notify")) {
    return null;
  }
  try {
    const tail = s.indexOf("?") >= 0 ? s.split("?").slice(1).join("?") : s;
    const q = new URLSearchParams(tail.replace(/^&/, ""));
    const ev =
      q.get("Event") ||
      q.get("event") ||
      q.get("Events") ||
      q.get("events");
    if (!ev || String(ev).toLowerCase() !== CHARGE_SERVICE_QUOTA_NOTIFY_EVENT) {
      return null;
    }
    const fake = {
      spu_name: q.get("spu_name"),
      spu_id: q.get("spu_id"),
      total_used_quota: q.get("total_used_quota"),
      total_quota: q.get("total_quota"),
      event_type: q.get("event_type"),
      appid: q.get("appid"),
      CreateTime: q.get("CreateTime"),
    };
    return buildChargeServiceQuotaNotifySummary(fake);
  } catch {
    return null;
  }
}

/** 小程序付费能力有效期告警 */
const CHARGE_SERVICE_VALIDITY_NOTIFY_EVENT = "charge_service_validity_notify";

function pickValidityEndHint(ctx) {
  if (!ctx || !isPlainObject(ctx)) return null;
  const keys = [
    "expire_time",
    "expireTime",
    "end_time",
    "endTime",
    "validity_end",
    "validity_end_time",
    "validityEndTime",
    "service_end_time",
    "serviceEndTime",
  ];
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    if (ctx[k] != null && String(ctx[k]).trim() !== "") {
      return String(ctx[k]).trim();
    }
  }
  return null;
}

function buildChargeServiceValidityNotifySummary(ctx) {
  if (!ctx || !isPlainObject(ctx)) return null;
  const lines = ["小程序付费能力有效期告警。"];
  const spuName =
    ctx.spu_name != null
      ? String(ctx.spu_name).trim()
      : ctx.spuName != null
        ? String(ctx.spuName).trim()
        : "";
  const spuId =
    ctx.spu_id != null
      ? String(ctx.spu_id).trim()
      : ctx.spuId != null
        ? String(ctx.spuId).trim()
        : "";
  if (spuName || spuId) {
    const namePart = spuName || "—";
    const idPart = spuId ? `（spu_id：${spuId}）` : "";
    lines.push(`组件：${namePart}${idPart}`);
  }
  const validityHint = pickValidityEndHint(ctx);
  if (validityHint) {
    lines.push(`有效期/到期相关：${validityHint}`);
  }
  const et =
    ctx.event_type != null
      ? ctx.event_type
      : ctx.eventType != null
        ? ctx.eventType
        : null;
  if (et != null && String(et).trim() !== "") {
    lines.push(`告警类型（event_type）：${String(et).trim()}`);
  }
  const appid =
    ctx.appid != null
      ? String(ctx.appid).trim()
      : ctx.AppId != null
        ? String(ctx.AppId).trim()
        : "";
  if (appid) {
    lines.push(`AppID：${appid}`);
  }
  const ct =
    ctx.CreateTime != null
      ? ctx.CreateTime
      : ctx.create_time != null
        ? ctx.create_time
        : null;
  if (ct != null && String(ct).trim() !== "") {
    lines.push(`时间（CreateTime）：${String(ct).trim()}`);
  }
  return lines.join("\n");
}

function summarizeChargeServiceValidityNotifyFromObject(obj) {
  const ctx = findEventContextRecursive(
    obj,
    CHARGE_SERVICE_VALIDITY_NOTIFY_EVENT,
    0
  );
  if (!ctx) return null;
  return buildChargeServiceValidityNotifySummary(ctx);
}

/** 纯文本 / querystring：charge_service_validity_notify */
function trySummarizeChargeServiceValidityNotifyPlainText(s) {
  if (
    !s ||
    typeof s !== "string" ||
    !s.includes("charge_service_validity_notify")
  ) {
    return null;
  }
  try {
    const tail = s.indexOf("?") >= 0 ? s.split("?").slice(1).join("?") : s;
    const q = new URLSearchParams(tail.replace(/^&/, ""));
    const ev =
      q.get("Event") ||
      q.get("event") ||
      q.get("Events") ||
      q.get("events");
    if (!ev || String(ev).toLowerCase() !== CHARGE_SERVICE_VALIDITY_NOTIFY_EVENT) {
      return null;
    }
    const fake = {
      spu_name: q.get("spu_name"),
      spu_id: q.get("spu_id"),
      event_type: q.get("event_type"),
      appid: q.get("appid"),
      CreateTime: q.get("CreateTime"),
      expire_time: q.get("expire_time"),
      end_time: q.get("end_time"),
      validity_end_time: q.get("validity_end_time"),
    };
    return buildChargeServiceValidityNotifySummary(fake);
  } catch {
    return null;
  }
}

/** 纯文本 / querystring 形式的 Event=wx_verify_dispatch */
function trySummarizeWxVerifyDispatchPlainText(s) {
  if (!s || typeof s !== "string" || !s.includes("wx_verify_dispatch")) {
    return null;
  }
  try {
    const tail = s.indexOf("?") >= 0 ? s.split("?").slice(1).join("?") : s;
    const q = new URLSearchParams(tail.replace(/^&/, ""));
    const ev =
      q.get("Event") ||
      q.get("event") ||
      q.get("Events") ||
      q.get("events");
    if (!ev || String(ev).toLowerCase() !== WX_VERIFY_DISPATCH_EVENT) {
      return null;
    }
    return buildWxVerifyDispatchLine(
      q.get("Contact") || q.get("contact"),
      q.get("Provider") || q.get("provider")
    );
  } catch {
    return null;
  }
}

/** 纯文本 / querystring：Event=user_enter_tempsession */
function trySummarizeUserEnterTempsessionPlainText(s) {
  if (!s || typeof s !== "string" || !s.includes("user_enter_tempsession")) {
    return null;
  }
  try {
    const tail = s.indexOf("?") >= 0 ? s.split("?").slice(1).join("?") : s;
    const q = new URLSearchParams(tail.replace(/^&/, ""));
    const ev =
      q.get("Event") ||
      q.get("event") ||
      q.get("Events") ||
      q.get("events");
    if (!ev || String(ev).toLowerCase() !== USER_ENTER_TEMPSESSION_EVENT) {
      return null;
    }
    const fake = {
      SessionFrom: q.get("SessionFrom") || q.get("session_from"),
    };
    return buildUserEnterTempsessionSummary(fake);
  } catch {
    return null;
  }
}

/** 无 Event/Events 但有 MsgType：系统消息或客服消息 */
function hasNonEmptyEventKey(obj) {
  if (!isPlainObject(obj)) return false;
  const keys = ["Event", "event", "Events", "events"];
  for (let i = 0; i < keys.length; i++) {
    const v = obj[keys[i]];
    if (v != null && String(v).trim() !== "") return true;
  }
  return false;
}

function getMsgTypeValue(obj) {
  if (!isPlainObject(obj)) return null;
  const mt =
    obj.MsgType != null
      ? obj.MsgType
      : obj.msg_type != null
        ? obj.msg_type
        : obj.msgtype != null
          ? obj.msgtype
          : null;
  if (mt == null || String(mt).trim() === "") return null;
  return String(mt).trim();
}

function findMsgTypeWithoutEventContext(obj, depth) {
  const d = depth == null ? 0 : depth;
  if (d > 5 || !isPlainObject(obj)) return null;
  const mt = getMsgTypeValue(obj);
  if (mt && !hasNonEmptyEventKey(obj)) {
    return obj;
  }
  const keys = Object.keys(obj);
  for (let i = 0; i < keys.length; i++) {
    const val = obj[keys[i]];
    if (isPlainObject(val)) {
      const found = findMsgTypeWithoutEventContext(val, d + 1);
      if (found) return found;
    }
    if (Array.isArray(val)) {
      for (let j = 0; j < val.length; j++) {
        if (isPlainObject(val[j])) {
          const found = findMsgTypeWithoutEventContext(val[j], d + 1);
          if (found) return found;
        }
      }
    }
  }
  return null;
}

function pickWechatMessageBodyLines(ctx) {
  if (!isPlainObject(ctx)) return [];
  const lines = [];
  const c = ctx.Content != null ? ctx.Content : ctx.content;
  if (c != null && String(c).trim() !== "") {
    lines.push(`内容：${String(c).trim()}`);
  }
  if (ctx.Title != null && String(ctx.Title).trim() !== "") {
    lines.push(`标题：${String(ctx.Title).trim()}`);
  }
  if (ctx.Description != null && String(ctx.Description).trim() !== "") {
    lines.push(`摘要：${String(ctx.Description).trim()}`);
  }
  if (ctx.Url != null && String(ctx.Url).trim() !== "") {
    lines.push(`链接：${String(ctx.Url).trim()}`);
  }
  if (ctx.PicUrl != null && String(ctx.PicUrl).trim() !== "") {
    lines.push(`图片：${String(ctx.PicUrl).trim()}`);
  }
  if (ctx.PagePath != null && String(ctx.PagePath).trim() !== "") {
    lines.push(`页面路径：${String(ctx.PagePath).trim()}`);
  }
  if (ctx.MediaId != null && String(ctx.MediaId).trim() !== "") {
    lines.push(`媒体 ID：${String(ctx.MediaId)}`);
  }
  if (ctx.MsgId != null && String(ctx.MsgId).trim() !== "") {
    lines.push(`消息 ID：${String(ctx.MsgId)}`);
  }
  return lines;
}

function buildMsgTypeWithoutEventSummary(ctx) {
  const mt = getMsgTypeValue(ctx);
  if (!mt) return null;
  const header = "接收到的系统消息或客服消息。";
  const typeLine = `类型（MsgType）：${mt}`;
  const bodyLines = pickWechatMessageBodyLines(ctx);
  if (bodyLines.length) {
    return [header, typeLine, ...bodyLines].join("\n");
  }
  return `${header}\n${typeLine}`;
}

function summarizeMsgTypeWithoutEventFromObject(obj) {
  const ctx = findMsgTypeWithoutEventContext(obj, 0);
  if (!ctx) return null;
  return buildMsgTypeWithoutEventSummary(ctx);
}

/** querystring：仅有 MsgType、无 Event 时 */
function trySummarizeMsgTypeWithoutEventPlainText(s) {
  if (!s || typeof s !== "string") return null;
  if (!/MsgType|msgtype|msg_type/i.test(s)) return null;
  try {
    const tail = s.indexOf("?") >= 0 ? s.split("?").slice(1).join("?") : s;
    const q = new URLSearchParams(tail.replace(/^&/, ""));
    const ev =
      q.get("Event") ||
      q.get("event") ||
      q.get("Events") ||
      q.get("events");
    if (ev != null && String(ev).trim() !== "") return null;
    const mt = q.get("MsgType") || q.get("msgtype") || q.get("msg_type");
    if (!mt || !String(mt).trim()) return null;
    const fake = {
      MsgType: mt,
      Content: q.get("Content") || q.get("content"),
      Title: q.get("Title"),
      Description: q.get("Description"),
      Url: q.get("Url"),
      PicUrl: q.get("PicUrl"),
      PagePath: q.get("PagePath"),
      MediaId: q.get("MediaId"),
      MsgId: q.get("MsgId"),
    };
    return buildMsgTypeWithoutEventSummary(fake);
  } catch {
    return null;
  }
}

function isPlainObject(v) {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

/** 从对象上按常见键取第一条非空字符串 */
function pickMessageString(obj) {
  if (!isPlainObject(obj)) return null;
  for (let i = 0; i < MSG_KEYS.length; i++) {
    const k = MSG_KEYS[i];
    const v = obj[k];
    if (v == null || v === "") continue;
    if (typeof v === "string") {
      const s = v.trim();
      if (s) return s;
    }
    if (typeof v === "number" && !Number.isNaN(v)) {
      return String(v);
    }
  }
  return null;
}

function deepFindMessage(obj, depth) {
  const d = depth == null ? 0 : depth;
  if (d > 5 || !isPlainObject(obj)) return null;

  const direct = pickMessageString(obj);
  if (direct) return direct;

  for (let i = 0; i < NEST_KEYS.length; i++) {
    const nk = NEST_KEYS[i];
    const child = obj[nk];
    if (isPlainObject(child)) {
      const inner = deepFindMessage(child, d + 1);
      if (inner) return inner;
    }
    if (typeof child === "string" && child.trim()) {
      try {
        const parsed = JSON.parse(child);
        if (isPlainObject(parsed)) {
          const inner = deepFindMessage(parsed, d + 1);
          if (inner) return inner;
        }
      } catch {
        /* ignore */
      }
    }
  }

  return null;
}

function getErrcode(obj) {
  if (!isPlainObject(obj)) return undefined;
  if (obj.errcode !== undefined && obj.errcode !== "") return obj.errcode;
  if (obj.errCode !== undefined && obj.errCode !== "") return obj.errCode;
  if (obj.code !== undefined && obj.code !== "" && typeof obj.code === "number")
    return obj.code;
  return undefined;
}

function formatErrcodeHint(obj) {
  const code = getErrcode(obj);
  if (code === undefined || code === null || code === "") return "";
  const n = Number(code);
  if (!Number.isNaN(n) && n === 0) return "成功";
  return `错误码 ${String(code)}`;
}

/**
 * 无标准文案时，用少量键值拼一句可读说明（仍避免整段 JSON）
 */
function fallbackReadablePairs(obj) {
  if (!isPlainObject(obj)) return null;
  const parts = [];
  const tryKeys = [
    ["action", "操作"],
    ["event", "事件"],
    ["type", "类型"],
    ["status", "状态"],
    ["state", "状态"],
    ["auditid", "审核单"],
    ["taskid", "任务"],
    ["appid", "AppID"],
    ["id", "ID"],
  ];
  for (let i = 0; i < tryKeys.length; i++) {
    const key = tryKeys[i][0];
    const label = tryKeys[i][1];
    const v = obj[key];
    if (v == null || v === "") continue;
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
      parts.push(`${label}：${String(v)}`);
    }
    if (parts.length >= 3) break;
  }
  if (parts.length) return parts.join("；");
  return null;
}

function summarizeArray(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  const pieces = [];
  const max = Math.min(arr.length, 3);
  for (let i = 0; i < max; i++) {
    const item = arr[i];
    if (typeof item === "string") {
      const t = item.trim();
      if (t) pieces.push(t.length > 60 ? `${t.slice(0, 60)}…` : t);
    } else if (isPlainObject(item)) {
      const m =
        pickMessageString(item) ||
        deepFindMessage(item, 0) ||
        fallbackReadablePairs(item);
      if (m) pieces.push(m);
    }
  }
  if (!pieces.length) return `共 ${arr.length} 条记录`;
  const more = arr.length > 3 ? ` 等共 ${arr.length} 条` : "";
  return `${pieces.join("；")}${more}`;
}

function parseJsonMaybe(raw) {
  if (raw == null || raw === "") return { ok: false, value: null };
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t) return { ok: false, value: null };
    try {
      return { ok: true, value: JSON.parse(t) };
    } catch {
      return { ok: false, value: raw };
    }
  }
  return { ok: true, value: raw };
}

/**
 * 将 admin_micoapp_logs.data（JSON 或字符串）整理为简短展示文案
 */
export function summarizeMicoappLogData(raw) {
  if (raw == null || raw === "") return "—";

  let parsed = parseJsonMaybe(raw);
  let obj = parsed.value;

  if (!parsed.ok) {
    const s = String(obj).trim();
    if (!s) return "—";
    const plainDispatch = trySummarizeWxVerifyDispatchPlainText(s);
    if (plainDispatch) return plainDispatch;
    const plainPaySucc = trySummarizeWxVerifyPaySuccPlainText(s);
    if (plainPaySucc) return plainPaySucc;
    const plainNicknameAudit = trySummarizeWxaNicknameAuditPlainText(s);
    if (plainNicknameAudit) return plainNicknameAudit;
    const plainWeappAuditSucc = trySummarizeWeappAuditSuccessPlainText(s);
    if (plainWeappAuditSucc) return plainWeappAuditSucc;
    const plainWeappAuditFail = trySummarizeWeappAuditFailPlainText(s);
    if (plainWeappAuditFail) return plainWeappAuditFail;
    const plainChargeQuota = trySummarizeChargeServiceQuotaNotifyPlainText(s);
    if (plainChargeQuota) return plainChargeQuota;
    const plainChargeValidity = trySummarizeChargeServiceValidityNotifyPlainText(
      s
    );
    if (plainChargeValidity) return plainChargeValidity;
    const plainTempsession = trySummarizeUserEnterTempsessionPlainText(s);
    if (plainTempsession) return plainTempsession;
    const plainMsgType = trySummarizeMsgTypeWithoutEventPlainText(s);
    if (plainMsgType) return plainMsgType;
    return s.length > 120 ? `文本记录：${s.slice(0, 120)}…` : `文本记录：${s}`;
  }

  if (typeof obj === "string") {
    const inner = parseJsonMaybe(obj);
    if (inner.ok && inner.value !== obj) {
      return summarizeMicoappLogData(inner.value);
    }
    const t = obj.trim();
    return t.length > 120 ? `文本：${t.slice(0, 120)}…` : `文本：${t}`;
  }

  if (typeof obj === "number" || typeof obj === "boolean") {
    return String(obj);
  }

  if (Array.isArray(obj)) {
    const line = summarizeArray(obj);
    return line || "列表数据（无可用摘要）";
  }

  if (!isPlainObject(obj)) {
    return String(obj);
  }

  const wxVerifyLine = summarizeWxVerifyDispatchFromObject(obj);
  if (wxVerifyLine) return wxVerifyLine;

  const wxVerifyPaySuccLine = summarizeWxVerifyPaySuccFromObject(obj);
  if (wxVerifyPaySuccLine) return wxVerifyPaySuccLine;

  const wxaNicknameAuditLine = summarizeWxaNicknameAuditFromObject(obj);
  if (wxaNicknameAuditLine) return wxaNicknameAuditLine;

  const weappAuditSuccessLine = summarizeWeappAuditSuccessFromObject(obj);
  if (weappAuditSuccessLine) return weappAuditSuccessLine;

  const weappAuditFailLine = summarizeWeappAuditFailFromObject(obj);
  if (weappAuditFailLine) return weappAuditFailLine;

  const chargeQuotaNotifyLine = summarizeChargeServiceQuotaNotifyFromObject(obj);
  if (chargeQuotaNotifyLine) return chargeQuotaNotifyLine;

  const chargeValidityNotifyLine =
    summarizeChargeServiceValidityNotifyFromObject(obj);
  if (chargeValidityNotifyLine) return chargeValidityNotifyLine;

  const tempsessionLine = summarizeUserEnterTempsessionFromObject(obj);
  if (tempsessionLine) return tempsessionLine;

  const msgTypeLine = summarizeMsgTypeWithoutEventFromObject(obj);
  if (msgTypeLine) return msgTypeLine;

  const msg =
    pickMessageString(obj) ||
    deepFindMessage(obj, 0) ||
    (isPlainObject(obj.data) ? deepFindMessage(obj.data, 1) : null);

  const errHint = formatErrcodeHint(obj);

  if (msg) {
    if (errHint && errHint !== "成功" && !/错误码/.test(msg)) {
      return `${msg}（${errHint}）`;
    }
    if (errHint === "成功" && (msg === "ok" || msg === "OK")) {
      return "成功";
    }
    return msg;
  }

  if (errHint) {
    return errHint;
  }

  const fallback = fallbackReadablePairs(obj);
  if (fallback) return fallback;

  const nested = NEST_KEYS.map((k) => obj[k]).find(
    (v) => isPlainObject(v) || Array.isArray(v)
  );
  if (isPlainObject(nested)) {
    const sub = summarizeMicoappLogData(nested);
    if (sub && sub !== "—") return `详情：${sub}`;
  }
  if (Array.isArray(nested)) {
    const sub = summarizeArray(nested);
    if (sub) return `详情：${sub}`;
  }

  const keys = Object.keys(obj);
  if (keys.length === 0) return "空对象";
  return `已记录 ${keys.length} 项字段（无标准说明文案）`;
}

/** 将表字段 time（文本/时间戳）转为可用于 timeago 的毫秒时间戳 */
export function parseMicoappLogTimeMs(t) {
  if (t == null || t === "") return null;
  if (typeof t === "number" && !Number.isNaN(t)) {
    return t < 1e12 ? t * 1000 : t;
  }
  const s = String(t).trim();
  const n = Number(s);
  if (!Number.isNaN(n) && /^\d+$/.test(s)) {
    return s.length <= 10 ? n * 1000 : n;
  }
  const d = Date.parse(s);
  return Number.isNaN(d) ? null : d;
}
